import { NgFor, NgIf } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
    AfterViewInit,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    ViewChild,
} from '@angular/core';
import { loadConnectAndInitialize, StripeConnectInstance } from '@stripe/connect-js';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { defer, of } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { PAYHOA_URLS } from '../../../../config/payhoa-urls';
import { StripeConnectService } from '../../../../services/stripe-treasury/stripe-connect.service';
import { ValidationMessageService } from '../../../../services/validation-message.service';
import { GrowlerService } from '../../../shared/growler/growler.service';
import { STRIPE_APPEARANCE_CONFIG } from '../../../v2-layout/stripe-treasury/stripe-treasury.constants';

/**
 * Shown when our API refuses the account but the response carries no reason we can surface. Names
 * the profile as the place to look, since that is the only data this request sends.
 */
const DEFAULT_REJECTION_MESSAGE = 'Stripe could not accept your organization details. Please review your organization settings and try again.';

/**
 * Shown when the verification form itself could not be brought up — Stripe's SDK failed to load, or
 * mounting threw. Deliberately says nothing about the organization's data, which is not implicated.
 */
const DEFAULT_LOAD_FAILURE_MESSAGE = 'We could not load the verification form. Please try again.';

/**
 * `ValidationMessageService.extractResponseErrorText` joins multiple API messages with this literal
 * string. Angular interpolation escapes it, so a two-reason rejection would render the markup as
 * text; split on it and give each reason its own line in the alert.
 */
const VALIDATION_MESSAGE_SEPARATOR = '<br />';

export interface BankAccountStripeOnboardingDialogInput {
    organizationId: number;

    /**
     * Is this organization being CONVERTED onto Treasury rather than joining it from scratch?
     *
     * ⚠️ IT CHANGES EXACTLY ONE THING — the collection options, which is what keeps the
     * payout-account step off the end. Everything else about the dialog is shared, deliberately.
     * Undefined for every caller but the Treasury page during a conversion.
     */
    isConversion?: boolean;
}

export interface BankAccountStripeOnboardingDialogResult {
    exited: boolean;
    error?: boolean;
    /**
     * The admin chose "Review organization settings" on the rejection state. The dialog reports the
     * intent rather than routing itself: the Treasury page records the resulting connect status
     * first, and navigating away destroys it; the add-bank journey opens a new tab instead, so a
     * form the admin is part-way through is not left behind.
     */
    goToSettings?: boolean;
}

/**
 * Stripe's embedded Connect onboarding. Asked through `BankAccountStripeOnboardingDialogStateService`
 * by the add-bank journey and by the Treasury page; the flow host creates this component for each
 * opening, never reopens a live one.
 *
 * Stripe's element is appended only once `createConnectAccount` has answered, by which time the
 * dialog has lifted itself to the body-level layer. Moving an iframe reloads it, so it must be born
 * where it stays.
 */
@UntilDestroy()
@Component({
    selector: 'app-bank-account-stripe-onboarding-dialog',
    templateUrl: './bank-account-stripe-onboarding-dialog.component.html',
    styleUrls: ['./bank-account-stripe-onboarding-dialog.component.scss'],
    standalone: true,
    imports: [NgIf, NgFor],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class BankAccountStripeOnboardingDialogComponent implements AfterViewInit, OnDestroy {
    @Input() open = false;
    @Input() organizationId: number;
    @Input() isConversion = false;
    @Output() readonly closed = new EventEmitter<BankAccountStripeOnboardingDialogResult>();

    @ViewChild('stripeContainer') stripeContainer: ElementRef<HTMLDivElement>;

    loading = true;

    /**
     * Stripe's reasons for refusing to create the account, or null while the form is on its way.
     * Held on the dialog rather than growled and dismissed: a reason names the exact field the
     * admin has to correct, and a 5-second toast is gone before they can act on it.
     */
    rejectionReasons: string[] | null = null;

    private _stripeConnectInstance: StripeConnectInstance | null = null;

    constructor(
        private _stripeConnectService: StripeConnectService,
        private _validationMessages: ValidationMessageService,
        private _growler: GrowlerService,
    ) {}

    ngAfterViewInit(): void {
        this.initializeStripeConnect();
    }

    close(): void {
        // A close from the rejection state is still a failed attempt — reporting it as a plain
        // exit would tell the Treasury page the org's status is worth re-reading, which it is not.
        this.closed.emit(this.rejectionReasons ? { exited: true, error: true } : { exited: true });
    }

    /**
     * Hand the admin to the page holding the field Stripe named. The rejection is always about
     * organization profile data we prefill (address, EIN, email, phone), all of which is edited on
     * the organization settings page — so there is one destination, not a per-field lookup.
     */
    goToOrganizationSettings(): void {
        this.closed.emit({ exited: true, error: true, goToSettings: true });
    }

    trackByIndex(index: number): number {
        return index;
    }

    ngOnDestroy(): void {
        if (this._stripeConnectInstance) {
            this._stripeConnectInstance.logout();
            this._stripeConnectInstance = null;
        }
    }

    private initializeStripeConnect(): void {
        this._stripeConnectService.createConnectAccount({
            organizationId: this.organizationId,
        }).pipe(
            switchMap((response) =>
                defer(async () => loadConnectAndInitialize({
                    publishableKey: response.publishableKey,
                    fetchClientSecret: async () => response.clientSecret,
                    appearance: STRIPE_APPEARANCE_CONFIG,
                })),
            ),
            tap((stripeConnect) => {
                this._stripeConnectInstance = stripeConnect;
                this.mountOnboardingComponent(stripeConnect);
            }),
            catchError((error) => {
                // Only a response from our own API can name a field of the organization profile.
                // This catch also covers Stripe's SDK failing to load and the mount throwing, and
                // blaming the org's details for either would send the admin to correct something
                // that is not wrong — so those growl and close.
                if (! (error instanceof HttpErrorResponse)) {
                    this._growler.oops(DEFAULT_LOAD_FAILURE_MESSAGE);
                    this.closed.emit({ exited: true, error: true });

                    return of(null);
                }

                this.rejectionReasons = this._validationMessages
                    .extractResponseErrorText(error, DEFAULT_REJECTION_MESSAGE)
                    .split(VALIDATION_MESSAGE_SEPARATOR);

                return of(null);
            }),
            finalize(() => this.loading = false),
            untilDestroyed(this),
        ).subscribe();
    }

    private mountOnboardingComponent(stripeConnect: StripeConnectInstance): void {
        const onboardingComponent = stripeConnect.create('account-onboarding');

        onboardingComponent.setFullTermsOfServiceUrl(PAYHOA_URLS.TERMS_OF_SERVICE);
        onboardingComponent.setPrivacyPolicyUrl(PAYHOA_URLS.PRIVACY_POLICY);

        // ⚠️ ONE DIFFERENCE FOR A CONVERSION, AND ONLY ONE: NO PAYOUT-ACCOUNT STEP. Richard: "it has
        // to look identical, and the only difference is, is it's not gonna go through the account
        // setup." `futureRequirements: 'include'` asks for everything Stripe will EVER want,
        // including confirming a payout destination a converting org already has (Richard hit it on
        // camera, 2026-10-08: "It's wanting me to add a bank account"). `currently_due` asks only for
        // what is outstanding. Narrowing the field set on a half-empty `business_profile` once
        // showed extra legal-name questions; the prefill now puts the account in the right shape.
        // An organization joining from scratch has no payout account, so it keeps `eventually_due`.
        onboardingComponent.setCollectionOptions(
            this.isConversion
                ? { fields: 'currently_due' }
                : { fields: 'eventually_due', futureRequirements: 'include' },
        );

        onboardingComponent.setOnStepChange((_stepChange) => {
            // Step change tracking — integrate with analytics when ready
        });

        onboardingComponent.setOnExit(() => this.close());

        if (this.stripeContainer?.nativeElement) {
            this.stripeContainer.nativeElement.appendChild(onboardingComponent);
        }
    }
}
